<template>
  <v-container fluid>
    <v-row v-if="isLoading">
      <v-col cols="12">
        <loading-spinner />
      </v-col>
    </v-row>
    <v-row v-else>
      <v-col cols="12">
        <v-card>
          <v-tabs v-model="activeTab" bg-color="primary" grow>
            <v-tab
              :href="'#' + tabs.review.id"
              v-if="finalReview && finalReview.status === 'COMPLETED'"
            >
              {{ $t("tabs.paper_review") }}
            </v-tab>

            <v-tab :href="'#' + tabs.author.id">
              {{ $t("tabs.author") }}
            </v-tab>

            <v-tab :href="'#' + tabs.content.id">
              {{ $t("tabs.paper_content") }}
            </v-tab>
          </v-tabs>

          <v-tabs-items v-model="activeTab">
            <v-tab-item
              :value="tabs.review.id"
              v-if="finalReview && finalReview.status === 'COMPLETED'"
            >
              <v-card flat>
                <v-card-text>
                  <show-paper-review
                    :final-review="finalReview"
                  ></show-paper-review>
                </v-card-text>
              </v-card>
            </v-tab-item>

            <v-tab-item :value="tabs.author.id">
              <v-card flat>
                <v-card-text>
                  <submit-author
                    :authors="paper.authors"
                    @add="saveAuthor"
                    @update="updateAuthor"
                    @delete="deleteAuthor"
                  ></submit-author>
                </v-card-text>
              </v-card>
            </v-tab-item>

            <v-tab-item :value="tabs.content.id">
              <v-card flat>
                <v-card-text>
                  <write-paper-content
                    :paper="paper"
                    :previous-papers="previousPapers"
                    @selectPreviousPaper="fetchPreviousPaper"
                    @update="handleUpdate"
                    @submit="handleSubmit"
                  ></write-paper-content>
                </v-card-text>
              </v-card>
            </v-tab-item>
          </v-tabs-items>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import LoadingSpinner from "@/components/common/Spinners/LoadingSpinner.vue";

import SubmitAuthor from "@/components/specific/Submission/SubmitAuthor.vue";
import ShowPaperReview from "@/components/specific/Submission/ShowPaperReview.vue";
import WritePaperContent from "@/components/specific/Submission/WritePaperContent.vue";

import { submissionTabs } from "@/fields/paper/tabs";

import { paperApi, authorApi, finalReviewApi, reviewApi } from "@/api/api";

export default {
  components: {
    SubmitAuthor,
    WritePaperContent,
    LoadingSpinner,
    ShowPaperReview,
  },
  props: {
    idx: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      tabs: submissionTabs,
      activeTab: submissionTabs.author.id,
      isLoading: false,
      paper: {
        idx: null,
        journal: {
          idx: null,
          title: "",
        },
        parent_idx: null,
        title: "",
        title_eng: "",
        keyword: "",
        keyword_eng: "",
        abstract: "",
        abstract_eng: "",
        authors: [],
        category: null,
        content: "",
      },
      finalReview: null,
      previousPapers: [],
    };
  },
  methods: {
    /**
     * ****************************************************************************************
     * Paper
     * ****************************************************************************************/
    async fetchPreviousPapers() {
      const params = {
        category: "PUBLICATION",
        status: "COMPLETED",
      };

      try {
        this.isLoading = true;
        const response = await paperApi.getPapersByMember(params);
        this.previousPapers = response?.data?.response;
      } catch (error) {
        this.notifyVue({
          message: this.$t("alert.failed_request"),
          style: "danger",
        });
        this.$router.push({ name: "dashboard" });
      } finally {
        this.isLoading = false;
      }
    },
    async fetchPaper(idx) {
      try {
        this.isLoading = true;
        const params = {
          idx: idx || this.idx,
        };

        const response = await paperApi.getPaper(params);

        if (response && response.status === 200) {
          return response.data.response;
        }
        return null;
      } catch {
        return null;
      } finally {
        this.isLoading = false;
      }
    },
    async updatePaper(paper) {
      try {
        const response = await paperApi.updatePaper(paper);
        if (response && response.status === 200) {
          this.paper = await this.fetchPaper();
          return true;
        }
        return false;
      } catch {
        this.notifyVue({
          message: this.$t("alert.failed_request"),
          style: "danger",
        });
        return false;
      }
    },
    async handleSubmit(paper) {
      try {
        if (this.paper.authors.length === 0) {
          this.notifyVue({
            message: this.$t("alert.author_required"),
            style: "danger",
          });
          return;
        }

        if (
          this.paper.category === "FINAL_REVISION" &&
          this.paper.status === "COMPLETED"
        ) {
          this.paper.category = "PUBLICATION";
          this.paper.status = "WAITING";
        }

        await this.updatePaper(paper);

        if (
          this.paper.category === "REVISION" &&
          this.paper.status != "COMPLETED"
        ) {
          await this.createReview();
          await this.createFinalReview();
        }

        await this.toSubmissionStatus();
      } catch {
        this.notifyVue({
          message: this.$t("alert.failed_request"),
          style: "danger",
        });
      }
    },
    async handleUpdate(updatedPaper) {
      await this.updatePaper(updatedPaper);
    },

    async toSubmissionStatus() {
      this.$router.push({
        name: "submission-status",
      });
    },
    async fetchPreviousPaper(paper) {
      const previousPaper = await this.fetchPaper(paper.idx);

      this.paper.title_eng = previousPaper.title_eng;
      this.paper.title = previousPaper.title;
      this.paper.abstract_eng = previousPaper.abstract_eng;
      this.paper.abstract = previousPaper.abstract;
      this.paper.keyword_eng = previousPaper.keyword_eng;
      this.paper.keyword = previousPaper.keyword;
      this.paper.subtitle_eng = previousPaper.subtitle_eng;
      this.paper.subtitle = previousPaper.subtitle;
      this.paper.content_eng = previousPaper.content_eng;
      this.paper.content = previousPaper.content;
    },
    /**
     * ****************************************************************************************
     * Author
     * ****************************************************************************************/
    async saveAuthor(author) {
      try {
        const data = {
          ...author,
          paper_idx: this.paper.idx,
        };
        const response = await authorApi.createAuthor(data);

        if (response && response.status === 201) {
          this.paper = await this.fetchPaper();
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        this.notifyVue({
          message: this.$t("alert.failed_request"),
          style: "danger",
        });
      }
    },
    async updateAuthor(author) {
      try {
        const response = await authorApi.updateAuthor(author);
        if (response && response.status === 200) {
          this.paper = await this.fetchPaper();
          return true;
        }
      } catch (error) {
        this.notifyVue({
          message: this.$t("alert.failed_request"),
          style: "danger",
        });
      }
    },
    async deleteAuthor(author) {
      try {
        const response = await authorApi.deleteAuthor(author);

        if (response && response.status === 204) {
          this.paper = await this.fetchPaper();
          return true;
        }
      } catch (error) {
        this.notifyVue({
          message: this.$t("alert.failed_request"),
          style: "danger",
        });
      }
    },
    /**
     * 총평 상세 데이터 api 요청
     */
    async fetchReviewByPaper() {
      try {
        this.isLoading = true;
        let paper_idx = this.paper.idx;

        if (
          this.paper.category === "REVISION" &&
          this.paper.status != "COMPLETED"
        ) {
          paper_idx = this.paper.parent_idx;
        }

        const params = {
          paper_idx: paper_idx,
        };
        const response = await finalReviewApi.showFinalReviewByPaper(params);

        if (response && response.status == 200) {
          this.finalReview = response.data.response;
        } else {
          this.notifyVue({
            alertMessage: this.$t("alert.failed_request"),
            color: "danger",
          });
        }
      } catch (error) {
        this.notifyVue({
          alertMessage: this.$t("alert.failed_request"),
          color: "danger",
        });
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * 할당 API 요청 메서드
     */
    async createReview() {
      const body = {
        paper_idx: this.paper.idx,
      };
      const response = await reviewApi.createReviewForResubmission(body);
      try {
        if (response && response.status === 201) {
          return true;
        } else {
          return false;
        }
      } catch {
        return false;
      }
    },

    async createFinalReview() {
      const body = {
        paper_idx: this.paper.idx,
        level: this.paper.level,
      };
      const response = await finalReviewApi.createFinalReview(body);
      try {
        if (response && response.status === 201) {
          return true;
        } else {
          return false;
        }
      } catch {
        return false;
      }
    },
    /* ****************************************************************************************
     * 알럿
     * ****************************************************************************************/
    notifyVue({
      vertical = "top",
      horizontal = "right",
      message = "",
      style = "primary",
      icon = "priority_high",
    } = {}) {
      this.$notify({
        timeout: 8000,
        message: message,
        icon: icon,
        type: style,
        horizontalAlign: horizontal,
        verticalAlign: vertical,
      });
    },
  },
  async mounted() {
    this.paper = await this.fetchPaper();
    await this.fetchReviewByPaper();
    await this.fetchPreviousPapers();
  },
};
</script>

<style scoped></style>
